import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TagNode } from './TagNode';
import { Summary, TagPeriod } from '../tag/Tag';
import { Entry } from '../entry/Entry';
import { EntryService } from '../entry/entry.service';
import { TagService } from '../tag/tag.service';
import { Tag } from '../tag/Tag';
import { DetailedEntry } from '../entry/EntryWithDetails';
import { DetailedTag } from '../tag/DetailedTag';
import { isParent } from 'src/utils/isParent';

export interface DefaultMapSettings {
  defaultColor: string;
  defaultSummary: Summary;
}

@Injectable({
  providedIn: 'root',
})
export class TagTreeMapper {
  constructor(private tagService: TagService) {}

  public static mapDetails(
    tags: Tag[],
    entries: Entry[],
    defaultMapSettings: DefaultMapSettings
  ): {
    tagNodes: TagNode[];
    tagsWithSummaryDetails: DetailedTag[];
    detailedEntries: DetailedEntry[];
  } {
    const combined = Array.from(new Set([...tags]));

    let subpaths: string[] = [];

    combined.forEach((tag) => {
      const path = tag.fullPath.split('/');
      for (let i = 1; i < path.length; i++) {
        const subpath = path.slice(0, i).join('/');
        if (!subpaths.includes(subpath)) {
          subpaths.push(subpath);
        }
      }
    });

    const allCombined = Array.from(
      new Set([...combined.map((t) => t.fullPath), ...subpaths])
    );

    const allCombinedWithExpenditure: DetailedTag[] = allCombined.map((tag) => {
      const fullPath = tag.split('/');
      return TagTreeMapper.calculateSummaryDetails(
        fullPath,
        tags,
        entries,
        defaultMapSettings
      );
    });

    const detailedEntries: DetailedEntry[] = entries.map((entry) => {
      const entryTags = tags.filter((tag) => entry.tagIds.includes(tag.id!));

      return {
        ...entry,
        tags: entryTags,
      };
    });

    return {
      tagNodes: TagTreeMapper.buildTree(
        allCombinedWithExpenditure.filter((t) => !t.isArchived)
      ),
      tagsWithSummaryDetails: allCombinedWithExpenditure,
      detailedEntries,
    };
  }

  private static getPlannedExpenditure(tag: string, tags: Tag[]): number {
    return (
      tags.find((currentTag) => currentTag.fullPath === tag)
        ?.plannedExpenditure || 0
    );
  }

  private static getPlannedIncome(tag: string, tags: Tag[]): number {
    return (
      tags.find((currentTag) => currentTag.fullPath === tag)?.plannedIncome || 0
    );
  }

  private static buildTree(
    allCombinedWithExpenditure: DetailedTag[]
  ): TagNode[] {
    let tree: TagNode[] = [];
    let n = 1;
    while (TagTreeMapper.levelExists(allCombinedWithExpenditure, n)) {
      tree = TagTreeMapper.buildLevel(allCombinedWithExpenditure, tree, n);
      n++;
    }
    return tree;
  }

  private static levelExists(
    tagsWithExpenditure: DetailedTag[],
    level: number
  ): boolean {
    return tagsWithExpenditure.some(
      (tag) => tag.fullPath.split('/').length === level
    );
  }

  private static buildLevel(
    tagsWithExpendiutre: DetailedTag[],
    tree: TagNode[],
    level: number
  ): TagNode[] {
    const tagsOnLevel = tagsWithExpendiutre.filter(
      (t) => t.fullPath.split('/').length === level
    );
    if (level === 1) {
      return tagsOnLevel.map((tag) => {
        return {
          name: tag.fullPath,
          fullPath: tag.fullPath,
          plannedExpenditure: tag.plannedExpenditure,
          plannedIncome: tag.plannedIncome,
          actualIncome: tag.actualIncome,
          actualExpenditure: tag.actualExpenditure,
          id: tag.id,
          color: tag.color,
          runningBalance: tag.runningBalance,
          isArchived: tag.isArchived,
          summary: tag.summary,
          isPeriodic: tag.isPeriodic,
          periods: tag?.periods ?? [],
          children: [],
        };
      });
    } else {
      tagsOnLevel.forEach((tag: DetailedTag) => {
        const parentNode: TagNode | null = TagTreeMapper.findParentNodeFor(
          tag.fullPath,
          tree
        );
        if (parentNode) {
          parentNode?.children!.push({
            fullPath: tag.fullPath,
            plannedExpenditure: tag.plannedExpenditure,
            actualExpenditure: tag.actualExpenditure,
            plannedIncome: tag.plannedIncome,
            actualIncome: tag.actualIncome,
            runningBalance: tag.runningBalance,
            children: [],
            id: tag.id,
            color: tag.color,
            summary: tag.summary,
            isPeriodic: tag.isPeriodic,
            periods: tag?.periods ?? [],
            isArchived: tag.isArchived,
          });
        }
      });
      return tree;
    }
  }

  private static findParentNodeFor(
    fullPath: string,
    tree: TagNode[]
  ): TagNode | null {
    if (tree.length === 0) {
      return null;
    }
    const parentNode = tree.find((node) => {
      {
        const targetParentPath = fullPath.split('/').slice(0, -1).join('/');

        return node.fullPath === targetParentPath;
      }
    });
    if (parentNode) {
      return parentNode;
    } else {
      const oneLevelDeeper = tree.map((node) => node.children ?? []).flat();
      return this.findParentNodeFor(fullPath, oneLevelDeeper);
    }
  }
  private static readonly FETCH_ALL_ENTRIES = true; // TODO: get this from user settings

  static calculateActualExpenditure(
    entries: Entry[],
    targetTagIds: (string | undefined)[],
    tag: Tag | undefined,
    allEntriesFetched = true
  ) {
    return allEntriesFetched
      ? entries
          .filter((e) => e.tagIds.some((t) => targetTagIds.includes(t)))
          .reduce((acc, entry) => {
            if (entry.amount && entry.amount < 0) {
              return acc + entry.amount * -1;
            } else {
              return acc;
            }
          }, 0)
      : tag?.actualExpenditure || 0;
  }

  static calculateActualIncome(
    entries: Entry[],
    targetTagIds: (string | undefined)[],
    tag: Tag | undefined,
    allEntriesFetched = true
  ) {
    return allEntriesFetched
      ? entries
          .filter((e) => e.tagIds.some((t) => targetTagIds.includes(t)))
          .reduce((acc, entry) => {
            if (entry.amount && entry.amount > 0) {
              return acc + entry.amount;
            } else {
              return acc;
            }
          }, 0)
      : tag?.actualIncome || 0;
  }

  static calculateRunnningBalance(
    entries: Entry[],
    targetTagIds: (string | undefined)[],
    tag: Tag | undefined,
    allEntriesFetched = true
  ) {
    return allEntriesFetched
      ? entries
          .filter((e) => e.tagIds.some((t) => targetTagIds.includes(t)))
          .reduce((acc, entry) => {
            return acc + entry.amount;
          }, 0)
      : tag?.actualIncome || 0;
  }

  private static calculateSummaryDetails(
    fullPath: string[],
    tags: Tag[],
    entries: Entry[],
    defaultMapSettings: DefaultMapSettings
  ): DetailedTag {
    const targetTagIds = tags
      .filter(
        (t) =>
          isParent(fullPath.join('/'), t.fullPath) ||
          fullPath.join('/') === t.fullPath
      )
      .map((t) => t.id);

    const tag = tags.find((tag) => tag.fullPath === fullPath.join('/'));

    const actualExpenditure = this.calculateActualExpenditure(
      entries,
      targetTagIds,
      tag
    );

    const actualIncome = this.calculateActualIncome(entries, targetTagIds, tag);

    const runningBalance = this.FETCH_ALL_ENTRIES
      ? entries
          .filter((e) => e.tagIds.some((t) => targetTagIds.includes(t)))
          .reduce((acc, entry) => acc + entry.amount, 0)
      : tag?.runningBalance || 0;

    const plannedExpenditure = this.getPlannedExpenditure(
      fullPath.join('/'),
      tags
    );
    const plannedIncome = this.getPlannedIncome(fullPath.join('/'), tags);

    return {
      fullPath: fullPath.join('/'),
      plannedExpenditure,
      id: tag?.id,
      actualExpenditure,
      actualIncome,
      runningBalance,
      plannedIncome,
      isPeriodic: tag?.isPeriodic,
      periods: this.getPeriodsWithCalculatedSummaries(
        tag?.periods ?? [],
        entries,
        targetTagIds,
        tag
      ),
      color: tag?.color ?? defaultMapSettings.defaultColor, // TODO: color might be missing
      isArchived: tag?.isArchived!,
      summary:
        tags.find((tagPlan) => tagPlan.fullPath === fullPath.join('/'))
          ?.summary ?? defaultMapSettings.defaultSummary,
    };
  }

  static getPeriodsWithCalculatedSummaries(
    periods: TagPeriod[],
    entries: Entry[],
    targetTagIds: (string | undefined)[],
    tag: Tag | undefined
  ): TagPeriod[] {
    return periods.map((p) => {
      const periodicEntries = entries.filter(
        (e) =>
          e.date?.getFullYear() === p.period.year &&
          e.date?.getMonth() + 1 === p.period.month
      );

      return {
        ...p,
        actualExpenditure: this.calculateActualExpenditure(
          periodicEntries,
          targetTagIds,
          tag
        ),
        actualIncome: this.calculateActualIncome(
          periodicEntries,
          targetTagIds,
          tag
        ),
      };
    });
  }
}
